import {message} from "antd";
import {AccountDeliveryDriverInput} from "pages/delivery/accounts/types/AccountDeliveryDriverInput";
import {getDspById} from "graphql/queries/getDspById";
import {
    createAccountHeadquartersStaff,
    updateAccountHeadquartersStaff
} from "../graphql/mutations/account/accountHeadquartersStaffMutations";
import {
    createAccountHeadquartersAdmin,
    updateAccountHeadquartersAdmin
} from "../graphql/mutations/account/accountHeadquartersAdminMutations";
import {
    createAccountFinancialTeam,
    updateAccountFinancialTeam
} from "../graphql/mutations/account/accountFinancialTeamMutations";
import {createAccountDspManager, updateAccountDspManager} from "../graphql/mutations/account/accountDspManagerMutations";
import {
    createAccountWarehouseManager,
    updateAccountWarehouseManager
} from "../graphql/mutations/account/accountWarehouseManagerMutations";
import {createAccountDeliveryDriverForPortal as createAccountDeliveryDriver} from "pages/delivery/accounts/graphql/mutations/createAccountDeliveryDriverForPortal";
import {updateAccountDeliveryDriver} from "pages/delivery/accounts/graphql/mutations/updateAccountDeliveryDriver";
import {
    createAccountWarehouseOperator,
    updateAccountWarehouseOperator
} from "../graphql/mutations/account/accountWarehouseOperatorMutations";
import {
    createAccountPickupDispatcher,
    updateAccountPickupDispatcher
} from "../graphql/mutations/account/accountPickupDispatcherMutations";
import {
    createAccountPickupStaff,
    updateAccountPickupStaff
} from "../graphql/mutations/account/accountPickupStaffMutations";
import {
    createAccountPickupDriver,
    updateAccountPickupDriver
} from "../graphql/mutations/account/accountPickupDriverMutations";
import {
    createAccountKAQC,
    updateAccountKAQC
} from "../graphql/mutations/account/accountKAQCMutations";

import {
    createAccountCS,
    updateAccountCS
} from "../graphql/mutations/account/accountCSMutations";

import {
    createAccountCSFollowUp,
    updateAccountCSFollowUp
} from "../graphql/mutations/account/accountCSFollowUpMutations";

import {
    createAccountConfigAdmin,
    updateAccountConfigAdmin
} from "../graphql/mutations/account/accountConfigAdminMutations";
import {
    createAccountRiskControl,
    updateAccountRiskControl
} from "../graphql/mutations/account/accountRiskControlMutations";

import {
    createAccountTmsDispatcher,
    updateAccountTmsDispatcher
} from "../graphql/mutations/account/accountTmsDispatcherMutations";
import {
    createAccountTmsDriver,
    updateAccountTmsDriver
} from "../graphql/mutations/account/accountTmsDriverMutations";
import { Dayjs } from "dayjs";
import { RoleType } from "graphql/types/AccountType";

export interface AccountFormData {
    id?: number;
    phone: {
        countryCode: number;
        phoneNumber: string;
    }
    name: string;
    email: string;
    role: RoleType;
    status: number;
    dspId?: number;
    dspCode?: string;
    warehouseId?: number;
    isOnDuty?: boolean;
    vehicleTypeId?: number;
    licensePlate?: string;
    vehiclePhotos?: string[];
    password?: string;
    // 揽收司机专用字段
    vehicleId?: number;
    workWeekdays?: number[]; // 工作日数组 [1,2,3,4,5]
    workHours?: [Dayjs | null, Dayjs | null]; // 工作时间范围
    workStartTime?: string;  // 工作开始时间 "09:00" (用于后端兼容)
    workEndTime?: string;    // 工作结束时间 "18:00" (用于后端兼容)
    responsibleZipcodes?: string[]; // 负责的邮编列表
    // TMS司机专用字段
    carrierId?: number; // 承运商ID
}

const createAccount = async (values: AccountFormData): Promise<boolean> => {
    console.log('createAccount', values);
    switch (values.role) {
        case 'HEADQUARTERS_STAFF':
            await createAccountHeadquartersStaff({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        case 'HEADQUARTERS_ADMIN':
            await createAccountHeadquartersAdmin({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        case 'FINANCIAL_TEAM':
        case 'DSP_FINANCIAL_TEAM':
        case 'INCOME_FINANCIAL_TEAM':
            await createAccountFinancialTeam({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'DSP_MANAGER':
            if (!values.dspId) {
                message.error('DSP ID is required for DSP Manager');
                return false;
            }
            await createAccountDspManager({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                dspId: values.dspId,
                role: values.role
            });
            break;
        case 'DSP_OWNER_TEAM':
            if (!values.dspId) {
                message.error('DSP ID is required for DSP Manager');
                return false;
            }
            await createAccountDspManager({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                dspId: values.dspId,
                role: values.role
            });
            break;
        case 'DELIVERY_DRIVER':
            console.log('createAccountDeliveryDriver', values);
            if (!values.dspId) {
                message.error('DSP ID is required for Delivery Driver');
                return false;
            }
            const dsp = await getDspById(values.dspId);
            if (!dsp) {
                message.error('DSP not found');
                return false;
            }
            const driverInput: AccountDeliveryDriverInput = {
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber || '',
                password: values.password,
                name: values.name,
                status: values.status,
                driverLicenseNumber: values.licensePlate,
                avatarStorageToken: undefined,
                vehicleTypeId: values.vehicleTypeId,
                vehiclePlate: values.licensePlate || '',
                vehicleImageS3KeysJson: values.vehiclePhotos || [],
                dspCode: dsp.dspCode,
                isOnDuty: values.isOnDuty || false
            };
            console.log('driverInput', driverInput);
            await createAccountDeliveryDriver(driverInput);
            break;

        case 'WAREHOUSE_MANAGER':
        case 'HUB_MANAGER':
            if (!values.warehouseId) {
                message.error('Warehouse ID is required for Warehouse Manager');
                return false;
            }
            await createAccountWarehouseManager({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'WAREHOUSE_OPERATOR':
        case 'HUB_OPERATOR':
        case 'SORTING_STAFF':
        case 'SORTER_OPERATOR':
        case 'RETURNS_STAFF':
        case 'WAREHOUSE_TAG_SORTER':
            if (!values.warehouseId) {
                message.error('Warehouse ID is required for Warehouse Operator');
                return false;
            }
            await createAccountWarehouseOperator({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_STAFF':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Staff');
                return false;
            }
            await createAccountPickupStaff({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_DISPATCHER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Dispatcher');
                return false;
            }
            await createAccountPickupDispatcher({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_DRIVER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Driver');
                return false;
            }

            // 调试日志
            console.log('Pickup Driver form values (create):', values);
            console.log('Responsible zipcodes (create):', values.responsibleZipcodes);

            const createPickupDriverInput = {
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                vehicleId: values.vehicleId,
                workWeekdaysJson: JSON.stringify(values.workWeekdays || [1, 2, 3, 4, 5]),
                workStartTime: values.workStartTime || '09:00:00',
                workEndTime: values.workEndTime || '18:00:00',
                responsibleZipcodeListJson: values.responsibleZipcodes && values.responsibleZipcodes.length > 0 ?
                    JSON.stringify(values.responsibleZipcodes) : "[]",
                role: values.role
            };

            console.log('Pickup Driver mutation input (create):', createPickupDriverInput);

            await createAccountPickupDriver(createPickupDriverInput);
            break;

        case 'KAQC':
            console.log('createAccountKAQC', values);
            await createAccountKAQC({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'CS':
            console.log('createAccountCS', values);
            await createAccountCS({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'CS_FOLLOW_UP':
            console.log('createAccountCSFollowUp', values);
            await createAccountCSFollowUp({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;
        case 'TMS_DISPATCHER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Tms Dispatcher');
                return false;
            }
            await createAccountTmsDispatcher({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;
        case 'TMS_DRIVER':
            if (!values.carrierId) {
                message.error('Carrier is required for TMS Driver');
                return false;
            }

            const createTmsDriverInput = {
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                vehicleId: values.vehicleId ? Number(values.vehicleId) : undefined,
                carrierId: Number(values.carrierId),
                role: values.role
            };

            await createAccountTmsDriver(createTmsDriverInput);
            break;

        case 'CONFIG_ADMIN':
            await createAccountConfigAdmin({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'RISK_CONTROL':
            await createAccountRiskControl({
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        default:
            // 使用通用创建账号接口
            message.error('Unsupported role');
            return false;
    }
    return true;
};

const updateAccount = async (values: AccountFormData): Promise<boolean> => {
    console.log('updateAccount', values);

    if (!values.id) {
        message.error('Personnel ID is required');
        return false;
    }

    switch (values.role) {
        case 'HEADQUARTERS_STAFF':
            await updateAccountHeadquartersStaff({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        case 'HEADQUARTERS_ADMIN':
            await updateAccountHeadquartersAdmin({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        case 'FINANCIAL_TEAM':
        case 'DSP_FINANCIAL_TEAM':
        case 'INCOME_FINANCIAL_TEAM':
            await updateAccountFinancialTeam({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'DSP_MANAGER':
            if (!values.dspId) {
                message.error('DSP ID is required for DSP Manager');
                return false;
            }
            await updateAccountDspManager({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                dspId: values.dspId,
                role: values.role
            });
            break;

        case 'DSP_OWNER_TEAM':
            if (!values.dspId) {
                message.error('DSP ID is required for DSP Manager');
                return false;
            }
            await updateAccountDspManager({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                dspId: values.dspId,
                role: values.role
            });
            break;

        case 'DELIVERY_DRIVER':
            if (!values.dspId) {
                message.error('DSP ID is required for Delivery Driver');
                return false;
            }
            const dsp = await getDspById(values.dspId);
            if (!dsp) {
                message.error('DSP not found');
                return false;
            }
            const driverInput: AccountDeliveryDriverInput = {
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber || '',
                password: values.password,
                name: values.name,
                status: values.status,
                driverLicenseNumber: values.licensePlate,
                avatarStorageToken: undefined,
                vehicleTypeId: values.vehicleTypeId,
                vehiclePlate: values.licensePlate || '',
                vehicleImageS3KeysJson: values.vehiclePhotos || [],
                dspCode: dsp.dspCode,
                isOnDuty: values.isOnDuty || false
            };
            await updateAccountDeliveryDriver(values.id, driverInput);
            break;

        case 'WAREHOUSE_MANAGER':
        case 'HUB_MANAGER':
            if (!values.warehouseId) {
                message.error('Warehouse ID is required for Warehouse Manager');
                return false;
            }
            await updateAccountWarehouseManager({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'WAREHOUSE_OPERATOR':
        case 'HUB_OPERATOR':
        case 'SORTING_STAFF':
        case 'SORTER_OPERATOR':
        case 'RETURNS_STAFF':
        case 'WAREHOUSE_TAG_SORTER':
            if (!values.warehouseId) {
                message.error('Warehouse ID is required for Warehouse Operator');
                return false;
            }
            await updateAccountWarehouseOperator({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_STAFF':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Staff');
                return false;
            }
            await updateAccountPickupStaff({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_DISPATCHER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Dispatcher');
                return false;
            }
            await updateAccountPickupDispatcher({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'PICKUP_DRIVER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Pickup Driver');
                return false;
            }

            // 调试日志
            console.log('Pickup Driver form values:', values);
            console.log('Responsible zipcodes:', values.responsibleZipcodes);

            const pickupDriverInput = {
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                vehicleId: values.vehicleId,
                workWeekdaysJson: JSON.stringify(values.workWeekdays || [1, 2, 3, 4, 5]),
                workStartTime: values.workStartTime || '09:00:00',
                workEndTime: values.workEndTime || '18:00:00',
                responsibleZipcodeListJson: values.responsibleZipcodes && values.responsibleZipcodes.length > 0 ?
                    JSON.stringify(values.responsibleZipcodes) : "[]",
                role: values.role
            };

            console.log('Pickup Driver mutation input:', pickupDriverInput);

            await updateAccountPickupDriver(pickupDriverInput);
            break;

        case 'KAQC':
            await updateAccountKAQC({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'CS':
            await updateAccountCS({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'CS_FOLLOW_UP':
            console.log('updateAccountCSFollowUp', values);
            await updateAccountCSFollowUp({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'CONFIG_ADMIN':
            await updateAccountConfigAdmin({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                role: values.role
            });
            break;

        case 'RISK_CONTROL':
            await updateAccountRiskControl({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status
            });
            break;

        case 'TMS_DISPATCHER':
            if (!values.warehouseId) {
                message.error('Hub ID is required for Tms Dispatcher');
                return false;
            }
            await updateAccountTmsDispatcher({
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                warehouseId: values.warehouseId,
                role: values.role
            });
            break;

        case 'TMS_DRIVER':
            if (!values.carrierId) {
                message.error('Carrier is required for TMS Driver');
                return false;
            }

            const tmsDriverUpdateInput = {
                id: values.id?.toString(),
                email: values.email,
                countryCode: values.phone.countryCode,
                phone: values.phone.phoneNumber,
                password: values.password || undefined,
                name: values.name,
                avatarStorageToken: undefined,
                status: values.status,
                vehicleId: values.vehicleId ? Number(values.vehicleId) : undefined,
                carrierId: Number(values.carrierId),
                role: values.role
            };

            await updateAccountTmsDriver(tmsDriverUpdateInput);
            break;

    }
    return true;
};

export const useAccountApis = () => {
    return {createAccount, updateAccount};
};